/**
 * Sentry.io Configuration for SmartAmbulance MVP Frontend
 * Real-time error tracking and performance monitoring for emergency healthcare systems
 */

// Import localStorage polyfill first to prevent build errors
import '../setup/localStoragePolyfill.js';

import * as Sentry from "@sentry/react";

// Initialize Sentry only if DSN is provided
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    // Priority: REACT_APP_SENTRY_ENVIRONMENT > NODE_ENV > default 'development'
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development',
    release: process.env.REACT_APP_SENTRY_RELEASE || `smartambulance-frontend@${process.env.REACT_APP_VERSION || '1.0.0'}`,
    
    // Performance monitoring
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE) || 1.0, // 100% in development, adjust for production
    
    // Browser tracing is automatically enabled in @sentry/react v10+
    // No need to explicitly add BrowserTracing integration
    
    // Filter sensitive data
    beforeSend(event, hint) {
      // Mark emergency-related errors as high priority
      if (event.tags?.component === 'EmergencyBookingInterface' || event.tags?.critical === 'true') {
        event.level = 'fatal';
        event.fingerprint = ['emergency-system-error'];
      }
      
      // Remove sensitive data from event
      if (event.request) {
        // Remove sensitive headers
        if (event.request.headers) {
          delete event.request.headers['authorization'];
          delete event.request.headers['cookie'];
        }
        
        // Remove sensitive query params
        if (event.request.query_string) {
          const queryParams = new URLSearchParams(event.request.query_string);
          queryParams.delete('token');
          queryParams.delete('password');
          event.request.query_string = queryParams.toString();
        }
      }
      
      // Remove sensitive data from extra context
      if (event.extra) {
        delete event.extra.password;
        delete event.extra.token;
        delete event.extra.authorization;
      }
      
      return event;
    },
    
    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Network errors
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      // Common non-critical errors — both known-benign ResizeObserver
      // browser warnings (a callback batch didn't finish within one
      // animation frame; functionality is unaffected either way)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
    
    // Set initial scope tags
    initialScope: {
      tags: {
        service: 'smartambulance-frontend',
        version: process.env.REACT_APP_VERSION || '1.0.0',
      },
    },
  });
  
  console.log('[SENTRY] Initialized successfully', {
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development',
    sentry_environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'not set',
    node_env: process.env.NODE_ENV || 'not set',
    dsn: process.env.REACT_APP_SENTRY_DSN ? 'configured' : 'missing',
  });
} else {
  console.warn('[SENTRY] DSN not provided, Sentry monitoring is disabled');
}

export default Sentry;

